import { MULTICITY, SEARCH_PLACE_TYPES } from '@web-platform/shared-universal-constants/src/places';
import { getPlaceType } from '@web-platform/shared-universal-utils-frontend/src/helpers/getPlaceType';

import type { SearchPlace } from '@web-platform/shared-universal-types/src/context';
import type { SearchPlaceType } from '@web-platform/shared-universal-types/src/requestContext';
import type {
  Distance,
  EntityId,
  Nullable,
  SkyNodeCode,
} from '@web-platform/shared-universal-types-utils/src/utils';

type GeneralSuggestion = {
  DistanceFromAirport: Nullable<Distance>;
  CityName?: string;
  CountryName?: string;
  GeoContainerId?: EntityId;
  PlaceName?: string;
  RegionId?: string;
};
type SuggestionParams = {
  CityId: Nullable<SkyNodeCode>;
  CityName: Nullable<string>;
  Containment?: Record<string, string>;
  CountryId?: SkyNodeCode;
  CountryName?: string;
  Distance?: Distance;
  Everywhere?: boolean;
  GeoContainerId?: EntityId;
  GeoId?: EntityId;
  GeneralSuggestion?: GeneralSuggestion;
  GeneralSearchResult?: boolean;
  HasChildren?: boolean;
  Highlighting?: number[][];
  IsChild?: boolean;
  // if explore Everywhere, PlaceId is Everywhere
  PlaceId: SkyNodeCode | SearchPlaceType;
  PlaceName: string;
  RegionId?: SkyNodeCode;
  ResultingPhrase?: string;
  ShouldShow?: boolean;
};

export default class Suggestion {
  CityId: Nullable<SkyNodeCode>;

  CityName: Nullable<string>;

  Containment: Nullable<Record<string, string>>;

  CountryId: Nullable<SkyNodeCode>;

  CountryName: Nullable<string>;

  Distance: Nullable<Distance>;

  Everywhere: boolean;

  GeoContainerId: EntityId | void;

  GeoId: EntityId | void;

  GeneralSuggestion: GeneralSuggestion | undefined;

  GeneralSearchResult: boolean;

  HasChildren: boolean;

  Highlighting: number[][];

  IsChild: boolean;

  PlaceId: SkyNodeCode;

  PlaceName: string;

  RegionId: SkyNodeCode | undefined;

  ResultingPhrase: Nullable<string>;

  ShouldShow: boolean;

  constructor(data: SuggestionParams) {
    this.CityId = data.CityId || null;
    this.CityName = data.CityName || null;
    this.Containment = data.Containment || null;
    this.CountryId = data.CountryId || null;
    this.CountryName = data.CountryName || null;
    this.Distance = data.Distance || null;
    this.Everywhere = data.Everywhere || false;
    this.GeoContainerId = data.GeoContainerId;
    this.GeoId = data.GeoId;
    this.GeneralSuggestion = data.GeneralSuggestion;
    this.GeneralSearchResult = data.GeneralSearchResult || false;
    this.HasChildren = data.HasChildren || false;
    this.Highlighting = data.Highlighting || [];
    this.IsChild = data.IsChild || false;
    this.PlaceId = data.PlaceId;
    this.PlaceName = data.PlaceName;
    this.RegionId = data.RegionId;
    this.ResultingPhrase = data.ResultingPhrase || null;
    this.ShouldShow = data.ShouldShow == null ? true : data.ShouldShow;
  }

  isEverywhere(): boolean {
    return this.Everywhere;
  }

  isMulticity(): boolean {
    return this.PlaceId === MULTICITY;
  }

  isGeneralSearchResult(): boolean {
    return this.GeneralSearchResult;
  }

  getType(): SearchPlaceType {
    if (this.Everywhere) {
      return SEARCH_PLACE_TYPES.EVERYWHERE;
    }
    if (this.PlaceId === MULTICITY) {
      return SEARCH_PLACE_TYPES.MULTICITY;
    }

    const place: SearchPlace = {
      id: this.PlaceId,
      name: this.PlaceName,
      cityId: this.CityId,
      countryId: this.CountryId,
    };

    return getPlaceType(place);
  }
}
